import React, { useCallback, useEffect, useRef, useState } from "react";
import { func, number, oneOfType, string } from "prop-types";

/**
 * Debounced text input, replacing the unmaintained `react-debounce-input`,
 * which has no React 19 release.
 *
 * Behaviour is kept the same as the library it replaces:
 *  - `onChange` fires `debounceTimeout` ms after typing stops
 *  - it only fires once the value reaches `minLength`
 *  - deleting back below `minLength` fires once with an empty value, so that
 *    clearing the box resets the search rather than leaving stale results
 *  - Enter and blur flush any pending change immediately
 *
 * @return {JSX.Element} the input element.
 */
const DebouncedInput = ({
  value = "",
  onChange,
  debounceTimeout = 0,
  minLength = 0,
  onKeyDown,
  onBlur,
  ...rest
}) => {
  const [innerValue, setInnerValue] = useState(value ?? "");
  const timer = useRef(null);
  const pending = useRef(null);

  useEffect(() => {
    setInnerValue(value ?? "");
  }, [value]);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    if (pending.current !== null) {
      const emitted = pending.current;
      pending.current = null;
      onChange({ target: { value: emitted } });
    }
  }, [onChange]);

  // Flush rather than drop a pending change when the input goes away.
  useEffect(() => () => clearTimeout(timer.current), []);

  const notify = (nextValue) => {
    pending.current = nextValue;
    clearTimeout(timer.current);
    if (debounceTimeout > 0) {
      timer.current = setTimeout(flush, debounceTimeout);
    } else {
      flush();
    }
  };

  const handleChange = (event) => {
    const nextValue = event.target.value;
    const previousValue = innerValue;
    setInnerValue(nextValue);

    if (nextValue.length >= minLength) {
      notify(nextValue);
    } else if (previousValue.length > nextValue.length) {
      // Backspacing below minLength counts as clearing the field.
      notify("");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      flush();
    }
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  const handleBlur = (event) => {
    flush();
    if (onBlur) {
      onBlur(event);
    }
  };

  return (
    <input
      {...rest}
      value={innerValue}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
    />
  );
};

DebouncedInput.propTypes = {
  value: oneOfType([string, number]),
  onChange: func.isRequired,
  debounceTimeout: number,
  minLength: number,
  onKeyDown: func,
  onBlur: func,
};

export default DebouncedInput;
